import { useEffect, useRef, useState } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material"
import { Upload, Image as ImageIcon, Megaphone, Calendar } from "lucide-react"

import TipTapEditor from "@crm-src/components/tiptap-editor"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { newsletterImagesApi } from "@crm-src/lib/newsletter-images-api"
import { useToast } from "@crm-src/hooks/use-toast"
import { generateMastheadHtml } from "./generate-html"
import {
  MASTHEAD_ACCENT,
  mastheadImageOptions,
  toPreviewImageUrl,
  type MastheadContent,
  type MastheadSection,
  type MastheadTenantInfo,
} from "./types"

interface MastheadNewsletterBuilderProps {
  tenantName: string
  tenantInfo?: MastheadTenantInfo
  initialSections?: MastheadSection[]
  onContentChange: (html: string) => void
  onSectionsChange?: (sections: MastheadSection[]) => void
}

// The template is a single fixed layout, so there is exactly ONE section. We
// still model it as a `sections` array (and round-trip it through
// `content_data.sections`) to match the webinar/olive builders' persistence
// contract in `content-editor.tsx`.
function defaultContent(): MastheadContent {
  return {
    announcement: "",
    masthead: "",
    title: "Solving Real Problems for Mission-Critical Networks",
    imageUrl: mastheadImageOptions[2]!.url,
    sponsorLogoUrl: "",
    date: "Thursday, July 9, 2026",
    startTime: "11:00am EDT | 8:00am PDT",
    sponsoredBy: "",
    registrationLink: "",
    unableToAttendNote: "All registrants will receive a link to watch on-demand after the live event.",
    body: "<p>Introduce your announcement or webinar here. Explain what recipients will learn and why it matters to them.</p><p>Use the toolbar to add links, lists, and emphasis — inline links pick up the brand color automatically.</p>",
  }
}

function defaultSection(): MastheadSection {
  return { id: "masthead", type: "masthead", content: defaultContent() }
}

// Merge a saved section over the defaults so newly-added fields (on an older
// saved campaign) fall back sensibly instead of rendering `undefined`.
function ensureSection(saved: MastheadSection[] | undefined): MastheadSection {
  const savedContent = saved?.find((s) => s.type === "masthead")?.content
  if (!savedContent) return defaultSection()
  return { id: "masthead", type: "masthead", content: { ...defaultContent(), ...savedContent } }
}

export function MastheadNewsletterBuilder({
  tenantName,
  tenantInfo,
  initialSections,
  onContentChange,
  onSectionsChange,
}: MastheadNewsletterBuilderProps) {
  const [section, setSection] = useState<MastheadSection>(() => ensureSection(initialSections))
  const [customImages, setCustomImages] = useState<Array<{ id: number; url: string; name: string }>>([])
  const [isUploadingHero, setIsUploadingHero] = useState(false)
  const [isUploadingSponsor, setIsUploadingSponsor] = useState(false)
  const heroInputRef = useRef<HTMLInputElement>(null)
  const sponsorInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  // Load previously-uploaded images so they appear next to the stock options.
  useEffect(() => {
    newsletterImagesApi
      .list()
      .then((images) =>
        setCustomImages(images.map((img, i) => ({ id: 5000 + i, url: img.url, name: img.name || "Custom Image" }))),
      )
      .catch((err) => console.error("Failed to load custom images:", err))
  }, [])

  // Re-hydrate when the parent supplies fresh initialSections (campaign switch).
  useEffect(() => {
    if (initialSections && initialSections.length > 0) {
      setSection(ensureSection(initialSections))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSections])

  // Short-circuit refs prevent the parent callbacks from looping the effect.
  const lastSectionsJsonRef = useRef<string>("")
  const lastHtmlRef = useRef<string>("")

  const update = (patch: Partial<MastheadContent>) =>
    setSection((prev) => ({ ...prev, content: { ...prev.content, ...patch } }))

  // Emit HTML + sections whenever the content changes.
  useEffect(() => {
    const json = JSON.stringify([section])
    if (json === lastSectionsJsonRef.current) return
    lastSectionsJsonRef.current = json

    const html = generateMastheadHtml(section, tenantInfo, new Date().getFullYear())
    if (html !== lastHtmlRef.current) {
      lastHtmlRef.current = html
      onContentChange(html)
    }
    onSectionsChange?.([section])
  }, [section, tenantInfo, onContentChange, onSectionsChange])

  const c = section.content

  const uploadImage = async (
    file: File,
    onDone: (url: string) => void,
    setBusy: (b: boolean) => void,
    resetInput: () => void,
  ) => {
    try {
      setBusy(true)
      const uploaded = await newsletterImagesApi.upload(file)
      onDone(uploaded.url)
      setCustomImages((prev) => [...prev, { id: 5000 + prev.length, url: uploaded.url, name: file.name }])
      toast({ title: "Success", description: "Image uploaded" })
    } catch (err) {
      console.error("Failed to upload image:", err)
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" })
    } finally {
      setBusy(false)
      resetInput()
    }
  }

  const labelSx = { fontSize: 14, fontWeight: 500, mb: 0.5, display: "block" } as const

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* ---- Announcement + masthead ---- */}
      <Card variant="outlined">
        <CardHeader
          title={
            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1rem", fontWeight: 600 }}>
              <Megaphone size={18} />
              Masthead
            </Box>
          }
        />
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
          <Box>
            <Typography component="label" sx={labelSx}>Announcement bar (optional)</Typography>
            <TextField
              fullWidth
              size="small"
              value={c.announcement}
              onChange={(e) => update({ announcement: e.target.value })}
              placeholder="e.g., Big news — a short italic line shown above the masthead"
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>Leave blank to hide the top bar.</Typography>
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>Masthead wordmark</Typography>
            <TextField
              fullWidth
              size="small"
              value={c.masthead}
              onChange={(e) => update({ masthead: e.target.value })}
              placeholder={tenantName}
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
              Shown in white on the brand-navy bar. Defaults to <strong>{tenantName}</strong>.
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Title</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="webinar invite headline — a concise, compelling title"
                  placeholder="e.g., securing mission-critical networks"
                  currentValue={c.title}
                  onGenerate={(v) => update({ title: v })}
                />
                <AiTextImprover fieldType="short" context="webinar invite headline" currentText={c.title} onImprove={(v) => update({ title: v })} />
              </Box>
            </Box>
            <TextField fullWidth size="small" value={c.title} onChange={(e) => update({ title: e.target.value })} placeholder="Headline" />
          </Box>
        </CardContent>
      </Card>

      {/* ---- Hero image + sponsor ---- */}
      <Card variant="outlined">
        <CardHeader
          title={
            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1rem", fontWeight: 600 }}>
              <ImageIcon size={18} />
              Hero image
            </Box>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Box
            component="img"
            src={toPreviewImageUrl(c.imageUrl) + "?w=610&h=240&fit=crop"}
            alt="Hero"
            sx={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 1, mb: 1.5, display: "block" }}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, mb: 1.5 }}>
            {[...mastheadImageOptions, ...customImages].map((img) => (
              <Box
                component="img"
                key={img.id}
                src={toPreviewImageUrl(img.url) + "?w=150&h=100&fit=crop"}
                alt={img.name}
                onClick={() => update({ imageUrl: img.url })}
                sx={{
                  width: "100%",
                  height: 56,
                  objectFit: "cover",
                  borderRadius: 1,
                  cursor: "pointer",
                  border: "2px solid",
                  borderColor: c.imageUrl === img.url ? MASTHEAD_ACCENT : "divider",
                }}
              />
            ))}
          </Box>
          <input
            ref={heroInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) uploadImage(file, (url) => update({ imageUrl: url }), setIsUploadingHero, () => { if (heroInputRef.current) heroInputRef.current.value = "" })
            }}
          />
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            fullWidth
            disabled={isUploadingHero}
            onClick={() => heroInputRef.current?.click()}
            startIcon={isUploadingHero ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
          >
            {isUploadingHero ? "Uploading…" : "Upload custom hero image"}
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography component="label" sx={labelSx}>Sponsor / "Presented by" logo (optional)</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {c.sponsorLogoUrl ? (
              <Box
                component="img"
                src={toPreviewImageUrl(c.sponsorLogoUrl) + "?w=180&h=48&fit=inside"}
                alt="Sponsor"
                sx={{ height: 40, width: "auto", maxWidth: 180, objectFit: "contain", borderRadius: 0.5, bgcolor: "background.default", p: 0.5, border: "1px solid", borderColor: "divider" }}
              />
            ) : (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No sponsor logo — the lockup is hidden.</Typography>
            )}
            <input
              ref={sponsorInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) uploadImage(file, (url) => update({ sponsorLogoUrl: url }), setIsUploadingSponsor, () => { if (sponsorInputRef.current) sponsorInputRef.current.value = "" })
              }}
            />
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              disabled={isUploadingSponsor}
              onClick={() => sponsorInputRef.current?.click()}
              startIcon={isUploadingSponsor ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
            >
              {isUploadingSponsor ? "Uploading…" : c.sponsorLogoUrl ? "Replace" : "Upload sponsor logo"}
            </Button>
            {c.sponsorLogoUrl && (
              <Button variant="text" color="error" size="small" onClick={() => update({ sponsorLogoUrl: "" })}>
                Remove
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ---- Event details + register ---- */}
      <Card variant="outlined">
        <CardHeader
          title={
            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1rem", fontWeight: 600 }}>
              <Calendar size={18} />
              Details &amp; registration
            </Box>
          }
        />
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5 }}>
            <Box>
              <Typography component="label" sx={labelSx}>Date</Typography>
              <TextField fullWidth size="small" value={c.date} onChange={(e) => update({ date: e.target.value })} placeholder="Thursday, July 9, 2026" />
            </Box>
            <Box>
              <Typography component="label" sx={labelSx}>Start time</Typography>
              <TextField fullWidth size="small" value={c.startTime} onChange={(e) => update({ startTime: e.target.value })} placeholder="11:00am EDT" />
            </Box>
            <Box>
              <Typography component="label" sx={labelSx}>Sponsored by</Typography>
              <TextField fullWidth size="small" value={c.sponsoredBy} onChange={(e) => update({ sponsoredBy: e.target.value })} placeholder="Partner name" />
            </Box>
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>
              Registration link <Box component="span" sx={{ color: "error.main" }}>*</Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              required
              value={c.registrationLink}
              onChange={(e) => update({ registrationLink: e.target.value })}
              placeholder="https://…"
              error={!c.registrationLink.trim()}
              helperText={
                !c.registrationLink.trim()
                  ? "Required to send — both “Register Now” buttons need a destination."
                  : "Drives both “Register Now” buttons."
              }
            />
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>"Unable to attend?" note (optional)</Typography>
            <TextField fullWidth size="small" value={c.unableToAttendNote} onChange={(e) => update({ unableToAttendNote: e.target.value })} placeholder="No problem — all registrants get the recording." />
          </Box>
        </CardContent>
      </Card>

      {/* ---- Body ---- */}
      <Card variant="outlined">
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box component="span" sx={{ fontSize: "1rem", fontWeight: 600 }}>Body</Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="long"
                  context="webinar invite body — explain what recipients will learn and why they should attend"
                  placeholder="e.g., the key topics and who should attend"
                  currentValue={(c.body || "").replace(/<[^>]*>/g, "")}
                  onGenerate={(v) => update({ body: `<p>${v}</p>` })}
                />
                <AiTextImprover
                  fieldType="long"
                  context="webinar invite body"
                  currentText={(c.body || "").replace(/<[^>]*>/g, "")}
                  onImprove={(v) => update({ body: `<p>${v}</p>` })}
                />
              </Box>
            </Box>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <TipTapEditor value={c.body} onChange={(html) => update({ body: html })} placeholder="Write your announcement…" />
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5 }}>
            The footer shows the XOP address, website, and the company social links (Facebook, LinkedIn, YouTube) automatically.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}

export default MastheadNewsletterBuilder
